import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useUserRole } from "@/hooks/useUserRole";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShieldAlert, TrendingDown, Users, Loader2, ArrowUpDown, ArrowUp, ArrowDown, Filter, Info, BarChart3 } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useLeaderboard, type LeaderboardEntry } from "@/hooks/useLeaderboard";
import { useInfluencerTickers } from "@/hooks/useInfluencerTickers";
import FiltersIndexRow from "@/components/FiltersIndexRow";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import UnifiedSearchBar from "@/components/UnifiedSearchBar";
import InfluencerCard from "@/components/InfluencerCard";
import FollowSubscriptionModal from "@/components/FollowSubscriptionModal";
import { Input } from "@/components/ui/input";
import { LeaderboardFilterRow, useLeaderboardFilters, filterInfluencer } from "@/components/LeaderboardFilters";
import { useGlobalInfluencerFilter } from "@/contexts/GlobalInfluencerFilterContext";

import SponsoredLeaderboardRow from "@/components/SponsoredLeaderboardRow";
import { ads } from "@/data/adData";

type LeaderboardSortKey = "score" | "avgPnl" | "avgPnlPerDay" | "winRate" | "netPnlCaptured" | "totalCalls";
type SortDir = "asc" | "desc";

const LeaderboardPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [tickerFilter, setTickerFilter] = useState("");
  const [sortKey, setSortKey] = useState<LeaderboardSortKey>("score");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const leaderboardRef = useRef<HTMLDivElement>(null);
  const { data: influencers = [], isLoading } = useLeaderboard();
  const { isAdmin, isPremium } = useUserRole();
  const { data: tickerMap } = useInfluencerTickers();
  const { user } = useAuth();
  const { filters, updateFilter, clearFilters, hasAnyFilter: hasColumnFilters, qualifyingIds: globalIds, hasAnyFilter: hasGlobalFilter } = useGlobalInfluencerFilter();

  // Batch-fetch follows
  const [followedIds, setFollowedIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!user) { setFollowedIds(new Set()); return; }
    supabase
      .from("follows")
      .select("x_user_id")
      .eq("user_id", user.id)
      .then(({ data }) => {
        setFollowedIds(new Set((data || []).map((f) => f.x_user_id)));
      });
  }, [user]);

  // Subscription modal state
  const [subModalOpen, setSubModalOpen] = useState(false);
  const [subModalHandle, setSubModalHandle] = useState("");

  const handleFollowToggle = useCallback(async (xUserId: string, handle: string) => {
    if (!user) {
      // Not logged in — send to login
      window.location.href = `/login?returnTo=${encodeURIComponent(window.location.pathname)}`;
      return;
    }
    if (!isPremium) {
      // Show subscription modal
      setSubModalHandle(handle);
      setSubModalOpen(true);
      return;
    }
    // Toggle follow
    const isCurrentlyFollowing = followedIds.has(xUserId);
    if (isCurrentlyFollowing) {
      await supabase.from("follows").delete().eq("user_id", user.id).eq("x_user_id", xUserId);
      setFollowedIds((prev) => { const next = new Set(prev); next.delete(xUserId); return next; });
      toast.success("Unfollowed");
    } else {
      await supabase.from("follows").insert({ user_id: user.id, x_user_id: xUserId });
      setFollowedIds((prev) => new Set(prev).add(xUserId));
      toast.success("Following!");
    }
  }, [user, isPremium, followedIds]);

  const leaderboardAds = ads;

  useEffect(() => {
    const ticker = searchParams.get("ticker");
    if (ticker) {
      setTickerFilter(ticker);
    }
  }, [searchParams]);

  const handleSort = (key: LeaderboardSortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    const tickers = tickerFilter
      .split(",")
      .map((t) => t.trim().toUpperCase())
      .filter(Boolean);

    const hasActiveFilter = q.length > 0 || tickers.length > 0;
    const MIN_CALLS = 10;

    return influencers
      .filter((i) => {
        // Global influencer filter
        if (hasGlobalFilter && !globalIds.has(i.id)) return false;

        const matchesSearch =
          i.handle.toLowerCase().includes(q) ||
          i.displayName.toLowerCase().includes(q);
        if (!matchesSearch) return false;

        if (!hasActiveFilter && !isAdmin && i.totalCalls < MIN_CALLS) return false;

        if (tickers.length > 0 && tickerMap) {
          const userTickers = tickerMap.get(i.id);
          if (!userTickers) return false;
          if (!tickers.some((t) => userTickers.has(t))) return false;
        }

        if (!filterInfluencer(i, filters)) return false;

        return true;
      })
      .sort((a, b) => {
        const MIN_CALLS = 10;
        const aLow = a.totalCalls < MIN_CALLS;
        const bLow = b.totalCalls < MIN_CALLS;
        if (aLow !== bLow) return aLow ? 1 : -1;
        const valA = sortKey === "score" ? a.score : sortKey === "netPnlCaptured" ? a.netPnlCaptured : a[sortKey];
        const valB = sortKey === "score" ? b.score : sortKey === "netPnlCaptured" ? b.netPnlCaptured : b[sortKey];
        const cmp = valA - valB;
        return sortDir === "asc" ? cmp : -cmp;
      });
  }, [searchQuery, tickerFilter, tickerMap, influencers, sortKey, sortDir, filters, hasGlobalFilter, globalIds]);

  // Synthetic "Filters Index" — aggregate of all filtered influencers
  const filtersIndex = useMemo(() => {
    if (filtered.length < 2) return null;
    const totalPositions = filtered.reduce((s, i) => s + i.totalCalls, 0);
    if (totalPositions === 0) return null;
    const totalActionable = filtered.reduce((s, i) => s + i.totalActionableCalls, 0);
    const weightedAvgPnl = filtered.reduce((s, i) => s + i.avgPnl * i.totalCalls, 0) / totalPositions;
    const weightedWinRate = filtered.reduce((s, i) => s + i.winRate * i.totalCalls, 0) / totalPositions;
    const weightedPnlPerDay = filtered.reduce((s, i) => s + i.avgPnlPerDay * i.totalCalls, 0) / totalPositions;
    const weightedDaysHeld = filtered.reduce((s, i) => s + i.avgDaysHeld * i.totalCalls, 0) / totalPositions;
    const netPnl = totalPositions * weightedAvgPnl;
    const score = Math.log10(Math.max(totalPositions, 1)) * weightedAvgPnl;
    return {
      id: "__filters_index__",
      handle: "FiltersIndex",
      displayName: "Filters Index",
      avatar: "",
      followers: "",
      totalCalls: totalPositions,
      totalActionableCalls: totalActionable,
      winRate: Math.round(weightedWinRate * 10) / 10,
      avgPnl: weightedAvgPnl,
      bestCall: Math.max(...filtered.map((i) => i.bestCall)),
      worstCall: Math.min(...filtered.map((i) => i.worstCall)),
      avgPnlPerDay: weightedPnlPerDay,
      avgDaysHeld: weightedDaysHeld,
      netPnlCaptured: netPnl,
      score,
      claimed: false,
    } satisfies LeaderboardEntry;
  }, [filtered]);

  // Insert the Filters Index at its natural sorted position
  const { rows, indexRank } = useMemo(() => {
    if (!filtersIndex) return { rows: filtered, indexRank: -1 };
    const sortVal = (e: LeaderboardEntry) =>
      sortKey === "score" ? e.score : sortKey === "netPnlCaptured" ? e.netPnlCaptured : e[sortKey];
    const idxVal = sortVal(filtersIndex);
    let pos = filtered.findIndex((i) => sortDir === "desc" ? sortVal(i) < idxVal : sortVal(i) > idxVal);
    if (pos === -1) pos = filtered.length;
    const merged = [...filtered];
    merged.splice(pos, 0, filtersIndex);
    return { rows: merged, indexRank: pos + 1 };
  }, [filtered, filtersIndex, sortKey, sortDir]);

  return (
    <main className="min-h-screen bg-background bg-grid">
      <section ref={leaderboardRef} className="w-[95vw] sm:w-[90vw] lg:w-[70vw] mx-auto px-4 pt-10 pb-60 min-h-[80vh] scroll-mt-24 relative overflow-visible">
        <div>
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2 className="text-2xl font-bold text-foreground">Global Leaderboard</h2>
                <p className="text-muted-foreground text-sm mt-1">
                  Ranked by historical call accuracy and average PnL.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <UnifiedSearchBar
                  influencers={influencers}
                  onSearch={setSearchQuery}
                  onNavigateToProfile={(handle) => navigate(`/profile/${handle.replace(/^@/, "")}`)}
                />
                <div className="relative w-full sm:w-64">
                  <Filter className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${tickerFilter.trim() ? "text-profit" : "text-muted-foreground"}`} />
                  <Input
                    placeholder="Filter by ticker, e.g. NVDA, AAPL"
                    value={tickerFilter}
                    onChange={(e) => setTickerFilter(e.target.value)}
                    className={`pl-9 font-mono text-xs h-9 transition-colors ${tickerFilter.trim() ? "border-profit/60 ring-1 ring-profit/30 text-profit" : ""}`}
                  />
                </div>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
            </div>
          ) : (
            <div className="bg-card border border-border rounded-xl overflow-x-auto">
              {/* Column Headers */}
              {influencers.length > 0 && (
                <>
                <div className="hidden md:grid grid-cols-[60px_minmax(160px,1fr)_100px_100px_100px_100px_110px_140px_40px] items-center gap-4 px-5 py-3 border-b border-border">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest flex items-center gap-1 cursor-help">Rank <Info className="w-2.5 h-2.5 opacity-50" /></span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs text-left space-y-1.5 p-3">
                      <p className="font-semibold text-xs">Rank</p>
                      <p className="text-[11px] text-muted-foreground"><span className="font-medium text-foreground">What it means:</span> The user's current standing against all other traders on the platform.</p>
                      <p className="text-[11px] text-muted-foreground"><span className="font-medium text-foreground">How it's calculated:</span> Ordered highest to lowest based on the custom Score.</p>
                      <p className="text-[11px] text-muted-foreground"><span className="font-medium text-foreground">Why it's important:</span> Quickly identifies the most proven, profitable traders at a glance.</p>
                    </TooltipContent>
                  </Tooltip>
                  <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-widest">Influencer</span>
                  {([
                    { key: "score" as LeaderboardSortKey, label: "Score", tooltip: {
                      title: "Score",
                      what: "Our proprietary rating that balances a trader's profitability with their experience level.",
                      how: "A formula weighing Average PnL against the total number of calls made.",
                      why: "It prevents lucky \"one-hit wonders\" from beating consistent, battle-tested traders.",
                    }},
                    { key: "avgPnl" as LeaderboardSortKey, label: "Avg PnL", tooltip: {
                      title: "Average Profit & Loss",
                      what: "The expected return of a single, typical trade from this user.",
                      how: "The sum of all trade return percentages divided by the total number of trades.",
                      why: "Shows the trader's true edge. A positive number means their core strategy works; a negative number means it loses money.",
                    }},
                    { key: "avgPnlPerDay" as LeaderboardSortKey, label: "PnL/day", tooltip: {
                      title: "Capital Velocity",
                      what: "Measures how fast your money grows or shrinks while tied up in a trade.",
                      how: "The percentage return of a trade divided by the days it was held, averaged across all trades.",
                      why: "When paired with Avg PnL, it acts as an X-ray into a trader's true style and discipline.",
                      extra: [
                        "High Positive → Fast-paced momentum trader or scalper.",
                        "Low Positive → Patient long-term investor, or a stubborn trader who holds losers to break even.",
                        "Negative (with positive Avg PnL) → Disciplined trader who lets winners ride but cuts losers fast.",
                        "Positive (with negative Avg PnL) → Red flag. Tiny quick profits hide massive losers.",
                      ],
                    }},
                    { key: "winRate" as LeaderboardSortKey, label: "Win Rate" },
                    { key: "netPnlCaptured" as LeaderboardSortKey, label: "Net PnL", tooltip: {
                      title: "Cumulative Return",
                      what: "The total theoretical return you would have if you followed every single trade.",
                      how: "The sum total of the percentage returns from all closed and open positions.",
                      why: "Shows the absolute wealth generated (or destroyed) by this trader over their entire history on the app.",
                    }},
                    { key: "totalCalls" as LeaderboardSortKey, label: "Calls / Pos", tooltip: {
                      title: "Calls / Positions",
                      what: "The total volume of trade alerts this user has published.",
                      how: "A simple count of all historical and active trades.",
                      why: "Proves experience. A high number shows success is based on a repeatable system, not just a few lucky guesses.",
                    }},
                  ] as const).map((col) => (
                    <Tooltip key={col.key}>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => handleSort(col.key)}
                          className={`font-mono text-[10px] uppercase tracking-widest text-center flex items-center justify-center gap-1 hover:text-foreground transition-colors ${
                            sortKey === col.key ? "text-foreground" : "text-muted-foreground"
                          }`}
                        >
                          {col.label}
                          {sortKey === col.key
                            ? sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                            : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                          {"tooltip" in col && col.tooltip && <Info className="w-2.5 h-2.5 opacity-50" />}
                        </button>
                      </TooltipTrigger>
                      {"tooltip" in col && col.tooltip ? (
                        <TooltipContent side="bottom" className="max-w-xs text-left space-y-1.5 p-3">
                          <p className="font-semibold text-xs">{col.tooltip.title}</p>
                          <p className="text-[11px] text-muted-foreground"><span className="font-medium text-foreground">What it means:</span> {col.tooltip.what}</p>
                          <p className="text-[11px] text-muted-foreground"><span className="font-medium text-foreground">How it's calculated:</span> {col.tooltip.how}</p>
                          <p className="text-[11px] text-muted-foreground"><span className="font-medium text-foreground">Why it's important:</span> {col.tooltip.why}</p>
                          {"extra" in col.tooltip && col.tooltip.extra && (
                            <ul className="text-[11px] text-muted-foreground space-y-0.5 mt-1 pl-3 list-disc">
                              {col.tooltip.extra.map((line: string, j: number) => <li key={j}>{line}</li>)}
                            </ul>
                          )}
                        </TooltipContent>
                      ) : <TooltipContent side="bottom"><p className="text-xs">{col.label}</p></TooltipContent>}
                    </Tooltip>
                  ))}
                  <span></span>
                </div>
                <LeaderboardFilterRow
                  filters={filters}
                  updateFilter={updateFilter}
                  clearFilters={clearFilters}
                  hasAnyFilter={hasColumnFilters}
                />
                </>
              )}
              {rows.map((influencer, i) => {
                const isIndex = influencer.id === "__filters_index__";
                if (isIndex) {
                  return <FiltersIndexRow key="__filters_index__" entry={influencer} rank={i + 1} influencerCount={filtered.length} onClick={() => navigate("/filters-index", { state: { ids: filtered.map((f) => f.id) } })} />;
                }
                return (
                  <div key={influencer.id} className="relative [&:has(button:hover)]:z-10">
                    <InfluencerCard
                      influencer={influencer}
                      rank={i + 1 - (filtersIndex && i + 1 > indexRank ? 1 : 0)}
                      onClick={() => navigate(`/profile/${influencer.handle.replace(/^@/, "")}`)}
                      followState={{
                        isFollowing: followedIds.has(influencer.id),
                        onToggle: () => handleFollowToggle(influencer.id, influencer.handle),
                      }}
                    />
                    {(() => {
                      // Calculate real rank (excluding index row) for ad placement
                      const realRank = i + 1 - (filtersIndex && i + 1 > indexRank ? 1 : 0);
                      const realCount = rows.length - (filtersIndex ? 1 : 0);
                      return realRank % 5 === 0 && realRank < realCount ? (
                        <SponsoredLeaderboardRow ad={leaderboardAds[Math.floor((realRank - 1) / 5) % ads.length]} />
                      ) : null;
                    })()}
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <div className="text-center py-12 text-muted-foreground font-mono text-sm">
                  {influencers.length === 0
                    ? "No influencers analyzed yet. Enter a handle above to get started!"
                    : `No influencers found matching "${searchQuery}"`}
                </div>
              )}
            </div>
          )}
        </div>
      </section>
      <FollowSubscriptionModal
        open={subModalOpen}
        onOpenChange={setSubModalOpen}
        handle={subModalHandle}
      />
    </main>
  );
};

export default LeaderboardPage;
